import { useQuery } from '@tanstack/react-query'
import { getAgentQueues } from '../transport/api'
import { QueueGroup, useQueueReasonsLive, useQueueSignal } from './QueueGroup'

/**
 * Every agent's held mail, as a tab inside Activity.
 *
 * A queue exists only while its recipient is busy, and the way you make an agent
 * un-busy is to go to it — so the Clean View panel was always in the view you had to
 * leave in order to end the block. This is reachable from anywhere, which is what the
 * signal needs: only a PERSON can clear one. A message queued behind a prompt cannot
 * release the prompt it is queued behind.
 *
 * Shares `QueueGroup` with the composer's queue sheet rather than restating the rows, so the two
 * cannot drift into disagreeing about what a waiting message looks like.
 */
export function QueueList() {
  const q = useQuery({
    queryKey: ['agent-queues'],
    queryFn: getAgentQueues,
  })
  // With the rail on Activity and nothing open in the stage, this tab is the ONLY reader on
  // screen — and it had no ears: the rail (Sidebar) and the bell are the ones that listen,
  // and neither is mounted then. Same shared lane, so it adds no read when they are.
  useQueueSignal()
  // This tab is a list of messages and why each waits; while it is on screen the reasons are kept true.
  useQueueReasonsLive(true)

  if (q.isPending) return <div className="act-empty">Loading…</div>
  if (q.isError) {
    // Named, not swallowed: an unreachable endpoint and an empty queue look identical
    // and mean opposite things.
    return <div className="act-empty act-err">Couldn’t read the queues.</div>
  }

  const queues = q.data?.queues ?? []
  if (queues.length === 0) {
    /*
     * Empty is the normal state and says so in words. A blank body reads as broken,
     * and this is the tab someone opens precisely when they suspect something is
     * stuck — the one moment a blank panel would be believed.
     */
    return (
      <div className="act-empty">
        No messages waiting.
        <div className="qs-hint">
          A message is held while its recipient is at a prompt, or its user is typing or
          speaking, and delivered on its own once they are free.
        </div>
      </div>
    )
  }

  return (
    <div className="qs-list">
      {queues.map((qu) => (
        <QueueGroup key={`${qu.to_host ?? ''} ${qu.to_session}`} qu={qu} />
      ))}
    </div>
  )
}
